import { useState, useEffect } from "react";
import "../styles/leads.css";
import api from "../Services/api";

export default function Vault() {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [password, setPassword] = useState("");

  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState([]);
  const [passwords, setPasswords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [noteTitle, setNoteTitle] = useState("");
  const [noteContent, setNoteContent] = useState("");

  const [previewFile, setPreviewFile] = useState(null);

  const [vaultRealPassword, setVaultRealPassword] = useState("1234");
  const [newVaultPassword, setNewVaultPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  // Password Manager State
  const [showAddPassword, setShowAddPassword] = useState(false);
  const [pwForm, setPwForm] = useState({ site: "", username: "", password: "", note: "" });
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [pwFormVisible, setPwFormVisible] = useState(false);
  const [searchPw, setSearchPw] = useState("");
  const [editingPw, setEditingPw] = useState(null);

  const fetchVaultConfig = async () => {
    try {
      const res = await api.get("organizations/me/");
      if (res.data.vault_password) {
        setVaultRealPassword(res.data.vault_password);
      }
      const userRes = await api.get("accounts/users/me/");
      const roles = userRes.data.rbac_roles_details || [];
      const isSysAdmin = userRes.data.is_superuser;
      const canManage = isSysAdmin || roles.some((r) => r.name.toLowerCase().includes("admin"));
      setIsAdmin(canManage);
    } catch (err) {
      console.error("Error fetching vault config:", err);
    }
  };

  const fetchVaultData = async () => {
    try {
      setLoading(true);
      const [filesRes, notesRes, pwRes] = await Promise.all([
        api.get("/vault/files/"),
        api.get("/vault/notes/"),
        api.get("/vault/passwords/"),
      ]);
      setFiles(filesRes.data);
      setNotes(notesRes.data);
      setPasswords(pwRes.data);
    } catch (err) {
      console.error("Error fetching vault data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVaultConfig();
  }, []);

  useEffect(() => {
    if (isUnlocked) {
      fetchVaultData();
    }
  }, [isUnlocked]);

  const handleUnlock = () => {
    if (password === vaultRealPassword) setIsUnlocked(true);
    else alert("Wrong Password ❌");
  };

  const updateVaultPassword = async () => {
    if (!newVaultPassword) return alert("Please enter a new password");
    try {
      await api.patch("organizations/me/", { vault_password: newVaultPassword });
      setVaultRealPassword(newVaultPassword);
      setNewVaultPassword("");
      alert("Vault password updated successfully! ✅");
    } catch (err) {
      console.error("Error updating vault password:", err);
      alert("Failed to update password. Check your permissions.");
    }
  };

  const handleUpload = async (e) => {
    const uploadedFiles = Array.from(e.target.files);
    try {
      for (const file of uploadedFiles) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("name", file.name);
        await api.post("/vault/files/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      fetchVaultData();
    } catch (err) {
      console.error("Error uploading vault files:", err);
    }
  };

  const deleteFile = async (id) => {
    try {
      await api.delete(`/vault/files/${id}/`);
      setFiles(files.filter((f) => f.id !== id));
    } catch (err) {
      console.error("Error deleting vault file:", err);
    }
  };

  const addNote = async () => {
    if (!noteTitle || !noteContent) return;
    try {
      await api.post("/vault/notes/", { title: noteTitle, content: noteContent });
      fetchVaultData();
      setNoteTitle("");
      setNoteContent("");
    } catch (err) {
      console.error("Error creating vault note:", err);
    }
  };

  const deleteNote = async (id) => {
    try {
      await api.delete(`/vault/notes/${id}/`);
      setNotes(notes.filter((n) => n.id !== id));
    } catch (err) {
      console.error("Error deleting vault note:", err);
    }
  };

  const renderPreview = (f) => {
    const type = f.file_type || "";
    if (type.startsWith("image")) return <img src={f.file} style={{ width: "100%" }} alt={f.name} />;
    if (type.startsWith("video")) return <video src={f.file} controls style={{ width: "100%" }} />;
    if (type === "application/pdf") return <iframe src={f.file} style={{ width: "100%", height: "400px" }} title={f.name} />;
    return <div style={{ fontSize: "40px" }}>📄</div>;
  };

  // ── Password Manager Handlers ──────────────────────────────────────────────

  const togglePasswordVisible = (id) => {
    setVisiblePasswords((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleSavePassword = async () => {
    if (!pwForm.site || !pwForm.username || !pwForm.password) {
      return alert("Site, Username aur Password required hain ⚠️");
    }

    const payload = {
      site: pwForm.site.trim(),
      username: pwForm.username.trim(),
      password: pwForm.password,
      note: pwForm.note.trim(),
    };

    console.log("Saving password payload:", payload);

    try {
      let res;
      if (editingPw) {
        res = await api.patch(`/vault/passwords/${editingPw}/`, payload);
        console.log("Password updated:", res.data);
      } else {
        res = await api.post("/vault/passwords/", payload);
        console.log("Password created:", res.data);
      }

      // Optimistically update local state without waiting for refetch
      if (editingPw) {
        setPasswords((prev) =>
          prev.map((p) => (p.id === editingPw ? { ...p, ...payload } : p))
        );
      } else {
        setPasswords((prev) => [...prev, res.data]);
      }

      setPwForm({ site: "", username: "", password: "", note: "" });
      setShowAddPassword(false);
      setEditingPw(null);
      setPwFormVisible(false);
    } catch (err) {
      console.error("Error saving password — full error:", err);
      console.error("Response data:", err?.response?.data);
      console.error("Status:", err?.response?.status);

      const detail =
        err?.response?.data
          ? JSON.stringify(err.response.data)
          : err.message || "Unknown error";

      alert(`Error: ${detail}`);
    }
  };

  const handleEditPassword = (pw) => {
    setPwForm({ site: pw.site, username: pw.username, password: pw.password, note: pw.note || "" });
    setEditingPw(pw.id);
    setShowAddPassword(true);
  };

  const handleDeletePassword = async (id) => {
    if (!window.confirm("Delete this saved password?")) return;
    try {
      await api.delete(`/vault/passwords/${id}/`);
      setPasswords(passwords.filter((p) => p.id !== id));
    } catch (err) {
      console.error("Error deleting password:", err);
    }
  };

  const filteredPasswords = passwords.filter(
    (p) =>
      p.site?.toLowerCase().includes(searchPw.toLowerCase()) ||
      p.username?.toLowerCase().includes(searchPw.toLowerCase())
  );

  // ── Favicon helper ─────────────────────────────────────────────────────────
  const getFavicon = (site) => {
    try {
      const domain = site.replace(/^https?:\/\//, "").split("/")[0];
      return `https://www.google.com/s2/favicons?sz=32&domain=${domain}`;
    } catch {
      return null;
    }
  };

  // ──────────────────────────────────────────────────────────────────────────

  if (!isUnlocked) {
    return (
      <div className="ld-root">
        <div className="ld-table-card" style={{ maxWidth: 400, margin: "120px auto", padding: 30 }}>
          <h2 className="ld-title" style={{ textAlign: "center" }}>🔐 Secure Vault</h2>
          <p style={{ textAlign: "center", fontSize: 13, color: "#64748b", marginTop: 8 }}>
            This area is encrypted. Enter your organization vault password to proceed.
          </p>
          <input
            type="password"
            className="ld-form-input"
            placeholder="Enter password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleUnlock()}
            style={{ marginTop: 25 }}
          />
          <button className="ld-add-btn" onClick={handleUnlock} style={{ marginTop: 15, width: "100%", padding: "12px" }}>
            Unlock Vault
          </button>

          {/* Show Admin Panel even when locked so they can reset password if forgotten */}
          {isAdmin && (
            <div style={{ marginTop: 30, padding: 15, background: "#fdf2f2", borderRadius: 10, border: "1px solid #fee2e2" }}>
              <h4 style={{ color: "#991b1b", fontSize: "12px", marginBottom: "10px" }}>🛡️ Admin: Change Vault Password</h4>
              <div style={{ display: "flex", gap: 5 }}>
                <input
                  type="text"
                  className="ld-form-input"
                  placeholder="New password"
                  value={newVaultPassword}
                  onChange={(e) => setNewVaultPassword(e.target.value)}
                  style={{ background: "white", fontSize: "12px", height: "32px" }}
                />
                <button
                  className="ld-add-btn"
                  onClick={updateVaultPassword}
                  style={{ background: "#dc2626", border: "none", color: "white", padding: "0 10px", fontSize: "11px", height: "32px" }}
                >
                  Update
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="ld-root">
      <div className="ld-header">
        <div className="ld-title-block">
          <span className="ld-eyebrow">SECURE VAULT</span>
          <h1 className="ld-title">Vault System</h1>
        </div>
        <label className="ld-add-btn">
          + Upload File
          <input type="file" multiple hidden onChange={handleUpload} />
        </label>
      </div>

      {/* ── Files + Notes Row ────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 30 }}>
        <div>
          <h3 style={{ marginBottom: 15, fontSize: 18 }}>📁 Stored Documents</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))",
              gap: 15,
              maxHeight: "60vh",
              overflowY: "auto",
              paddingRight: 10,
            }}
          >
            {loading ? (
              <div>Loading files...</div>
            ) : files.length === 0 ? (
              <div
                style={{
                  gridColumn: "1/-1",
                  textAlign: "center",
                  padding: 40,
                  background: "#f8fafc",
                  borderRadius: 12,
                  border: "1px dashed #e2e8f0",
                }}
              >
                <div style={{ fontSize: 30, marginBottom: 10 }}>📂</div>
                <div style={{ color: "#64748b", fontSize: 14 }}>No sensitive files found.</div>
              </div>
            ) : (
              files.map((f) => (
                <div key={f.id} className="ld-table-card" style={{ padding: 12, transition: "transform 0.2s", cursor: "default" }}>
                  <div
                    style={{
                      height: "130px",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#f1f5f9",
                      borderRadius: 8,
                    }}
                  >
                    {f.file_type?.startsWith("image") ? (
                      <img src={f.file} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt={f.name} />
                    ) : (
                      <div style={{ fontSize: "40px", textAlign: "center" }}>📄</div>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      marginTop: 10,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "#1e293b",
                    }}
                  >
                    {f.name}
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>{f.file_type}</div>
                  <div style={{ display: "flex", gap: 5, marginTop: 12 }}>
                    <button className="ld-action-btn" title="View" onClick={() => setPreviewFile(f)}>👁</button>
                    <a href={f.file} download className="ld-action-btn" title="Download">⬇</a>
                    <button className="ld-action-btn danger" title="Delete" onClick={() => deleteFile(f.id)}>🗑</button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div>
          <h3 style={{ marginBottom: 15, fontSize: 18 }}>📝 Secure Notepad</h3>
          <div className="ld-table-card" style={{ padding: 20, marginBottom: 20 }}>
            <input
              className="ld-form-input"
              placeholder="Entry Title (e.g. Server Credentials)"
              value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)}
            />
            <textarea
              className="ld-form-input ld-textarea"
              placeholder="Secret content / Passwords..."
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              style={{ marginTop: 12, minHeight: 100 }}
            />
            <button className="ld-add-btn" onClick={addNote} style={{ marginTop: 15, width: "100%" }}>
              Save Encryption
            </button>
          </div>
          <div style={{ maxHeight: "40vh", overflowY: "auto" }}>
            {notes.map((n) => (
              <div key={n.id} className="ld-table-card" style={{ padding: 15, marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <strong style={{ color: "#1e293b" }}>{n.title}</strong>
                  <button className="ld-action-btn danger" onClick={() => deleteNote(n.id)}>🗑</button>
                </div>
                <p style={{ fontSize: 13, marginTop: 8, color: "#475569", background: "#f8fafc", padding: 10, borderRadius: 6, borderLeft: "3px solid #cbd5e1" }}>
                  {n.content}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Password Manager Section ─────────────────────────────────────────── */}
      <div style={{ marginTop: 40 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h3 style={{ fontSize: 18, margin: 0 }}>🔑 Password Manager</h3>
            <span
              style={{
                background: "#eff6ff",
                color: "#3b82f6",
                fontSize: 11,
                fontWeight: 600,
                padding: "3px 10px",
                borderRadius: 20,
                border: "1px solid #bfdbfe",
              }}
            >
              {passwords.length} saved
            </span>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <input
              className="ld-form-input"
              placeholder="🔍 Search site or username..."
              value={searchPw}
              onChange={(e) => setSearchPw(e.target.value)}
              style={{ width: 220, margin: 0, padding: "8px 12px", fontSize: 13 }}
            />
            <button
              className="ld-add-btn"
              onClick={() => {
                setPwForm({ site: "", username: "", password: "", note: "" });
                setEditingPw(null);
                setShowAddPassword(true);
              }}
              style={{ whiteSpace: "nowrap" }}
            >
              + Add Password
            </button>
          </div>
        </div>

        {/* Password List */}
        <div className="ld-table-card" style={{ padding: 0, overflow: "hidden" }}>
          {/* Table Header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 2fr 2fr 2fr 1fr",
              gap: 0,
              background: "#f8fafc",
              padding: "12px 20px",
              borderBottom: "1px solid #e2e8f0",
              fontSize: 12,
              fontWeight: 600,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            <span>Site</span>
            <span>Username</span>
            <span>Password</span>
            <span>Note</span>
            <span style={{ textAlign: "right" }}>Actions</span>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading passwords...</div>
          ) : filteredPasswords.length === 0 ? (
            <div
              style={{
                padding: 50,
                textAlign: "center",
                color: "#94a3b8",
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 10 }}>🔒</div>
              <div style={{ fontSize: 14 }}>
                {searchPw ? "No passwords match your search." : "No passwords saved yet. Click '+ Add Password' to start."}
              </div>
            </div>
          ) : (
            filteredPasswords.map((pw, idx) => (
              <div
                key={pw.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 2fr 2fr 2fr 1fr",
                  gap: 0,
                  padding: "14px 20px",
                  borderBottom: idx < filteredPasswords.length - 1 ? "1px solid #f1f5f9" : "none",
                  alignItems: "center",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                {/* Site */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <img
                    src={getFavicon(pw.site)}
                    alt=""
                    style={{ width: 20, height: 20, borderRadius: 4, flexShrink: 0 }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                      {pw.site.replace(/^https?:\/\//, "").split("/")[0]}
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>{pw.site}</div>
                  </div>
                </div>

                {/* Username */}
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 13, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>
                    {pw.username}
                  </span>
                  <button
                    onClick={() => copyToClipboard(pw.username)}
                    title="Copy username"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 13,
                      color: "#94a3b8",
                      padding: "2px 4px",
                      borderRadius: 4,
                      flexShrink: 0,
                    }}
                  >
                    📋
                  </button>
                </div>

                {/* Password */}
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      fontSize: 13,
                      color: "#475569",
                      fontFamily: visiblePasswords[pw.id] ? "monospace" : "monospace",
                      letterSpacing: visiblePasswords[pw.id] ? "normal" : "2px",
                      maxWidth: 140,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {visiblePasswords[pw.id] ? pw.password : "••••••••••••"}
                  </span>
                  <button
                    onClick={() => togglePasswordVisible(pw.id)}
                    title={visiblePasswords[pw.id] ? "Hide" : "Show"}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#94a3b8", padding: "2px 4px", borderRadius: 4, flexShrink: 0 }}
                  >
                    {visiblePasswords[pw.id] ? "🙈" : "👁"}
                  </button>
                  <button
                    onClick={() => copyToClipboard(pw.password)}
                    title="Copy password"
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#94a3b8", padding: "2px 4px", borderRadius: 4, flexShrink: 0 }}
                  >
                    📋
                  </button>
                </div>

                {/* Note */}
                <div style={{ fontSize: 12, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>
                  {pw.note || <span style={{ fontStyle: "italic" }}>No note added</span>}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 5, justifyContent: "flex-end" }}>
                  <button className="ld-action-btn" title="Edit" onClick={() => handleEditPassword(pw)}>✏️</button>
                  <button className="ld-action-btn danger" title="Delete" onClick={() => handleDeletePassword(pw.id)}>🗑</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Admin Panel ──────────────────────────────────────────────────────── */}
      {isAdmin && (
        <div style={{ marginTop: 50, padding: 30, background: "#fdf2f2", borderRadius: 16, border: "1px solid #fee2e2" }}>
          <h3 style={{ marginBottom: 10, color: "#991b1b", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>🛡️</span> Administration Security Panel
          </h3>
          <p style={{ fontSize: 14, color: "#b91c1c", marginBottom: 20 }}>
            You can change the global vault password for your organization here. This will affect all staff members.
          </p>
          <div style={{ display: "flex", gap: 10, maxWidth: 450 }}>
            <input
              type="text"
              className="ld-form-input"
              placeholder="Set new vault password"
              value={newVaultPassword}
              onChange={(e) => setNewVaultPassword(e.target.value)}
              style={{ background: "white" }}
            />
            <button
              className="ld-add-btn"
              onClick={updateVaultPassword}
              style={{ background: "#dc2626", border: "none", color: "white", padding: "0 25px" }}
            >
              Update Password
            </button>
          </div>
        </div>
      )}

      {/* ── File Preview Modal ────────────────────────────────────────────────── */}
      {previewFile && (
        <div className="ld-modal-backdrop" onClick={() => setPreviewFile(null)}>
          <div className="ld-modal" style={{ maxWidth: "750px", width: "90%" }} onClick={(e) => e.stopPropagation()}>
            <div className="ld-modal-head">
              <h2 className="ld-modal-title">{previewFile.name}</h2>
              <button className="ld-modal-close" onClick={() => setPreviewFile(null)}>✕</button>
            </div>
            <div className="ld-modal-body">{renderPreview(previewFile)}</div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Password Modal ─────────────────────────────────────────── */}
      {showAddPassword && (
        <div className="ld-modal-backdrop" onClick={() => setShowAddPassword(false)}>
          <div
            className="ld-modal"
            style={{ maxWidth: 480, width: "90%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ld-modal-head">
              <h2 className="ld-modal-title">{editingPw ? "Edit Password" : "Add new password"}</h2>
              <button className="ld-modal-close" onClick={() => setShowAddPassword(false)}>✕</button>
            </div>
            <div className="ld-modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Site */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>Site</label>
                <input
                  className="ld-form-input"
                  placeholder="example.com"
                  value={pwForm.site}
                  onChange={(e) => setPwForm({ ...pwForm, site: e.target.value })}
                  style={{ margin: 0 }}
                />
              </div>

              {/* Username */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>Username</label>
                <input
                  className="ld-form-input"
                  placeholder="you@example.com"
                  value={pwForm.username}
                  onChange={(e) => setPwForm({ ...pwForm, username: e.target.value })}
                  style={{ margin: 0 }}
                />
              </div>

              {/* Password */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={pwFormVisible ? "text" : "password"}
                    className="ld-form-input"
                    placeholder="••••••••••"
                    value={pwForm.password}
                    onChange={(e) => setPwForm({ ...pwForm, password: e.target.value })}
                    style={{ margin: 0, paddingRight: 40 }}
                  />
                  <button
                    onClick={() => setPwFormVisible(!pwFormVisible)}
                    style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 16,
                      color: "#94a3b8",
                    }}
                  >
                    {pwFormVisible ? "🙈" : "👁"}
                  </button>
                </div>
                <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>
                  Make sure you're saving your current password for this site.
                </p>
              </div>

              {/* Note */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>Note</label>
                <textarea
                  className="ld-form-input ld-textarea"
                  placeholder="Optional note..."
                  value={pwForm.note}
                  onChange={(e) => setPwForm({ ...pwForm, note: e.target.value })}
                  style={{ margin: 0, minHeight: 80 }}
                />
              </div>

              {/* Actions */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
                <button
                  onClick={() => setShowAddPassword(false)}
                  style={{
                    background: "none",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    padding: "10px 20px",
                    cursor: "pointer",
                    fontSize: 14,
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </button>
                <button className="ld-add-btn" onClick={handleSavePassword}>
                  {editingPw ? "Update" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}